import { useTranslation } from 'react-i18next'

import { Box, TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { HiddenFieldLabel } from '~/components/common/HiddenFieldLabel'
import { FilterSection, FilterToolbar } from '~/components/common/filters'
import type { CivilDate } from '~/models/civilDate'

const PERIOD_FROM_INPUT_ID = 'jepxSettlement-periodFrom'
const PERIOD_TO_INPUT_ID = 'jepxSettlement-periodTo'

type Props = {
  from: CivilDate | undefined
  to: CivilDate | undefined
  onFromChange: (d: CivilDate | undefined) => void
  onToChange: (d: CivilDate | undefined) => void
  onClear: () => void
}

/**
 * 清算日の期間の絞り込み (AC-JST-002)。 指定中の期間と件数はツールバーの下の
 * `PeriodSummary` に出す (8080 `bidding/settlement.html` の `.stl-summary`)。
 *
 * 見出し「清算日」は入力と同じ行に置く (UI 辞書 `components/toolbar.md` の
 * `.filter-group` = ラベル + コントロールの横並び。 8080 も同じ)。 「期間をクリア」 は
 * 8080 と同じ Tertiary sm。
 *
 * 開始日・終了日 (どちらも含む) の 2 つの日付入力で、 各々任意。 初期は期間指定なし
 * (= すべて)。 期間はサーバー側で絞る (request の `settlement_date_from` / `_to`)。
 *
 * **開始日 > 終了日は入力の時点で作らせない**: 終了日の `min` に開始日、 開始日の `max` に
 * 終了日を渡す (`CompactCivilDateField` は popup で範囲外の日を選べなくし、 手入力された
 * 範囲外の値は境界へ寄せる)。 供給力ギャップ AC-SDG-008 (「終了日が開始日より前なら
 * 終了日を開始日に揃える」) と同じ向き。 backend は開始日 > 終了日を InvalidArgument にするので、
 * 画面がその request を送らないことが前提になる。
 */
export const PeriodFilter = ({
  from,
  to,
  onFromChange,
  onToChange,
  onClear,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const isPeriodSet = from !== undefined || to !== undefined

  return (
    <FilterToolbar>
      <FilterSection label={t('page:jepxSettlement.filter.period')}>
        <Box width={COMPACT_DATE_FIELD_WIDTH}>
          <HiddenFieldLabel htmlFor={PERIOD_FROM_INPUT_ID}>
            {t('page:jepxSettlement.filter.periodFrom')}
          </HiddenFieldLabel>
          <CompactCivilDateField
            hasClear
            id={PERIOD_FROM_INPUT_ID}
            max={to}
            onChange={onFromChange}
            value={from}
          />
        </Box>
        <Text color="Text.midEmphasis">〜</Text>
        <Box width={COMPACT_DATE_FIELD_WIDTH}>
          <HiddenFieldLabel htmlFor={PERIOD_TO_INPUT_ID}>
            {t('page:jepxSettlement.filter.periodTo')}
          </HiddenFieldLabel>
          <CompactCivilDateField
            hasClear
            id={PERIOD_TO_INPUT_ID}
            min={from}
            onChange={onToChange}
            value={to}
          />
        </Box>
        {isPeriodSet && (
          <TertiaryButton onClick={onClear} size="sm">
            {t('page:jepxSettlement.filter.clear')}
          </TertiaryButton>
        )}
      </FilterSection>
    </FilterToolbar>
  )
}
